<template>
  <!-- Class names are deliberately NOT re-prefixed to this component: the rendered class attribute
       must stay exactly what the two call sites' inline copies produced, because the PARENTS own
       the stylesheets. Renaming anything here silently unstyles both. -->
  <div :class="rootClass">
    <div class="v2026-sch-booking__head">
      <div class="v2026-sch-booking__ids">
        <a role="link" class="v2026-sch-link" :href="orderLink(order.id)" target="_blank">#{{ order.code }}</a>
        <span class="v2026-sch-booking__id">ID: {{ order.id }}</span>
      </div>
      <a class="v2026-sch-icon-btn" :href="orderLink(order.id)" target="_blank" aria-label="เปิดออเดอร์">
        <span class="v2026-sch-mask v2026-sch-edit" aria-hidden="true"
              :style="maskStyle(icons.externalLink)"></span>
      </a>
    </div>
    <div class="v2026-sch-booking__service" v-if="order.service">{{ order.service.name_th }}</div>
    <div class="v2026-sch-booking__row" v-if="order.work_hour">
      <span class="v2026-sch-mask v2026-sch-booking__ico" aria-hidden="true"
            :style="maskStyle(icons.bClock)"></span>
      <span class="v2026-sch-booking__text">{{ order.work_hour }} ชม.</span>
    </div>
    <div class="v2026-sch-booking__row" v-if="alwaysShowDate || order.cleaning_date">
      <span class="v2026-sch-mask v2026-sch-booking__ico" aria-hidden="true"
            :style="maskStyle(icons.bCalendar)"></span>
      <span class="v2026-sch-booking__text">{{ order.cleaning_date | prettyDateFull }}</span>
    </div>
    <!-- A clock icon on the province row looks wrong but is pre-existing in both call sites. -->
    <div class="v2026-sch-booking__row" v-if="order.province">
      <span class="v2026-sch-mask v2026-sch-booking__ico" aria-hidden="true"
            :style="maskStyle(icons.bClock)"></span>
      <span class="v2026-sch-booking__text">{{ order.province.name_th }}</span>
    </div>
    <div class="v2026-sch-booking__row" v-if="order.place_size">
      <span class="v2026-sch-mask v2026-sch-booking__ico" aria-hidden="true"
            :style="maskStyle(icons.bLocation)"></span>
      <span class="v2026-sch-booking__text">{{ order.place_size.name_th }}</span>
    </div>
    <div class="v2026-sch-booking__row" v-if="order.address">
      <span class="v2026-sch-mask v2026-sch-booking__ico"
            aria-hidden="true" :style="maskStyle(icons.bMapLocation)"></span>
      <span class="v2026-sch-booking__text v2026-sch-booking__text--danger">{{ order.address }}</span>
    </div>
    <!-- Slot content compiles in the PARENT scope, so it keeps matching the parent's rules. -->
    <slot />
  </div>
</template>

<script>
const ICONS = {
  externalLink: require('@/assets/icons2026/external-link.svg'),
  bClock: require('@/assets/icons2026/b-time.svg'),
  bCalendar: require('@/assets/icons2026/b-calendar.svg'),
  bLocation: require('@/assets/icons2026/b-location.svg'),
  bMapLocation: require('@/assets/icons2026/b-maps-location.svg')
}

export default {
  name: 'booking-card-v2026',
  props: {
    order: {
      type: Object,
      default: () => ({})
    },
    // 'plain' = the day list, 'card' = the latest-order card, which also gets .v2026-sch-card.
    variant: {
      type: String,
      default: 'plain',
      validator: v => ['plain', 'card'].includes(v)
    },
    // The two call sites genuinely differ: the latest-order card renders an icon-only row when the
    // date is falsy, the day card hides it. Preserved on purpose, not an oversight.
    alwaysShowDate: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      icons: ICONS
    }
  },
  computed: {
    // One binding, not static + :class, so the token ORDER in the rendered attribute is preserved.
    rootClass () {
      return this.variant === 'card' ? 'v2026-sch-card v2026-sch-booking' : 'v2026-sch-booking'
    }
  },
  methods: {
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    },
    orderLink (id) {
      return `https://admin-test.beneat.co/order/${id}`
    }
  }
}
</script>

<style scoped>
.v2026-sch-booking {
  --v2026-bc-color-main: #31C1D7;
  --v2026-bc-link: #007bff;
  --v2026-bc-surface-sunken: #F8FAFC;
  --v2026-bc-surface-hover: #e9ecf1;
  --v2026-bc-text: #101828;
  --v2026-bc-text-secondary: #667085;
  --v2026-bc-text-muted: #98a2b3;
  --v2026-bc-icon: #475467;
  --v2026-bc-r-card: 12px;
  --v2026-bc-sp-2: 8px;
  --v2026-bc-sp-3: 12px;
  --v2026-bc-fs-body: 14px;
  --v2026-bc-fs-meta: 12px;

  background: var(--v2026-bc-surface-sunken);
  border-radius: var(--v2026-bc-r-card);
  padding: var(--v2026-bc-sp-3);
  display: flex;
  flex-direction: column;
  gap: var(--v2026-bc-sp-2);
}

.v2026-sch-card {
  background: var(--v2026-bc-surface-sunken);
  border-radius: var(--v2026-bc-r-card);
}

.v2026-sch-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-bc-icon);
}

.v2026-sch-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-sch-icon-btn:hover {
  background: var(--v2026-bc-surface-hover);
}
.v2026-sch-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-sch-edit {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-bc-icon);
}

.v2026-sch-link {
  color: var(--v2026-bc-link);
  text-decoration: none;
  font-size: var(--v2026-bc-fs-body);
}
.v2026-sch-link:hover {
  text-decoration: underline;
}
.v2026-sch-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 3px;
}

.v2026-sch-booking__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--v2026-bc-sp-2);
}
.v2026-sch-booking__ids {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--v2026-bc-sp-2);
  min-width: 0;
}
.v2026-sch-booking__id {
  font-size: var(--v2026-bc-fs-meta);
  color: var(--v2026-bc-text-muted);
}
.v2026-sch-booking__service {
  font-size: var(--v2026-bc-fs-body);
  font-weight: 600;
  color: var(--v2026-bc-text);
}
.v2026-sch-booking__row {
  display: flex;
  align-items: flex-start;
  gap: var(--v2026-bc-sp-2);
}
.v2026-sch-booking__ico {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  margin-top: 1px;
  background-color: var(--v2026-bc-color-main);
}
.v2026-sch-booking__text {
  font-size: var(--v2026-bc-fs-meta);
  color: var(--v2026-bc-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-sch-booking__text--danger {
  color: #f04438;
}
</style>
